import { ReactNode } from "react";

import { AssistantContentBuilder } from "./AssistantMessage/AssistantContentBuilder";
import { ReasoningGroup } from "./AssistantMessage/ReasoningGroup";
import { ThoughtProcessGroup } from "./AssistantMessage/ThoughtProcessGroup";
import { VisibleItem } from "./part-processor";
import { SystemMessage } from "./SystemMessage/SystemMessage";
import { UserAttachmentGroup } from "./UserMessage/UserAttachmentGroup";
import { MessageFlags } from "@src/features/ai/components/WorkflowEditor/execution/types";
import { UserMessage } from "./UserMessage/UserMessage";

export type PartBuilderProps = {
  item: VisibleItem;
  index: number;
  isLast?: boolean;
  isLastTurn?: boolean;
  renderPart?: RenderPart;
} & MessageFlags;

/** Everything the default branches below decide from, minus the hook itself. */
export type RenderPartArgs = Omit<PartBuilderProps, "renderPart">;

/**
 * A consumer's first refusal on one item, before any default branch runs.
 *
 * Return `undefined` to decline, and the default rendering below happens
 * exactly as if the hook were absent. Return a node to own that item outright.
 *
 * **`null` is a node, not a decline.** The sentinel is `undefined` rather than
 * anything falsy precisely so that returning `null` means "render nothing
 * here" — which is the only way to drop a single item in place.
 * `transformMessage` drops parts earlier, at the message, and cannot express
 * "keep the part, render it as nothing".
 */
export type RenderPart = (args: RenderPartArgs) => ReactNode;

export const PartBuilder = (props: PartBuilderProps) => {
  const { renderPart, ...forwarded } = props;

  // Destructured out of `forwarded` on purpose: what is left is spread onto the
  // part components below, and a stray function prop would ride along into them.
  if (renderPart) {
    const rendered = renderPart(forwarded);
    if (rendered !== undefined) {
      return rendered;
    }
  }

  const { item, isLastTurn, ...rest } = forwarded;

  if (item.type === "reasoningGroup") {
    return <ReasoningGroup parts={item.parts} {...rest} />;
  }

  if (item.type === "thoughtProcessGroup") {
    return (
      <ThoughtProcessGroup
        parts={item.parts}
        isLastTurn={isLastTurn}
        {...rest}
      />
    );
  }

  if (item.type === "userAttachmentGroup") {
    return <UserAttachmentGroup item={item} />;
  }

  if (item.messageRole === "assistant") {
    return <AssistantContentBuilder item={item} {...rest} />;
  }

  if (item.messageRole === "user") {
    return <UserMessage part={item.part} {...rest} />;
  }

  if (item.messageRole === "system" && item.part.type === "text") {
    return <SystemMessage content={item.part.text} {...rest} />;
  }

  return null;
};
